import { Link } from "react-router-dom";
import { CheckCircle2, FileText, Sparkles, Target, Compass, HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { PRTemplateForm } from "@/components/site/PRTemplateForm";
import { Reveal } from "@/hooks/useReveal";

const SECTIONS = [
  {
    icon: Target,
    label: "Positioning worksheet",
    body: "One sentence a journalist could quote. Nail your audience, unique credential, and lens.",
  },
  {
    icon: Compass,
    label: "Four keyword clusters",
    body: "Fill in themes, target money pages, volumes, difficulty, and priority — one page per cluster.",
  },
  {
    icon: FileText,
    label: "90-day publish calendar",
    body: "Ten posts sequenced across three waves so quick wins ship first and pillar pieces come last.",
  },
  {
    icon: Sparkles,
    label: "Off-site PR angles + KPIs",
    body: "PR hooks per cluster, target outlets, and a one-page scoreboard for your weekly review.",
  },
];

const FAQS = [
  {
    q: "What is a PR strategy — and how is it different from a PR campaign?",
    a: "A PR strategy is the 90-day plan that decides which audiences you want to reach, which keyword clusters you'll own, and which money pages every story has to link back to. A campaign is one push inside that plan. The template forces you to write the strategy first so campaigns actually compound.",
  },
  {
    q: "Do I need Semrush or expensive tools to use this template?",
    a: "No. The worksheet works with free keyword data from Google, Answer the Public, and Search Console. If you already have Semrush or Ahrefs, there are dedicated cells for volume and difficulty — but they're optional.",
  },
  {
    q: "How long does it take to fill in?",
    a: "About 90 focused minutes for the first pass. Most founders do sections 1–3 (positioning, money pages, clusters) in one sitting and finish the 90-day calendar the next morning with a coffee.",
  },
  {
    q: "Who is this template actually built for?",
    a: "Founders, keynote speakers, executive coaches, and pastors leading fast-growing organizations who want earned media and organic search to feed the same pipeline. If you're pre-launch and don't yet know your offer, book a call instead — a template won't fix positioning.",
  },
  {
    q: "Will you email me constantly after I download it?",
    a: "No. You'll get the PDF, one short follow-up with the companion post, and an optional invite to a working session. Unsubscribe in one click. Your email is never sold or shared.",
  },
  {
    q: "Can I use this for a client or team, not just myself?",
    a: "Yes. Agencies and in-house comms leads use it as a kickoff worksheet. It's licensed for internal use — just don't resell it as your own product.",
  },
  {
    q: "What if I want Dr. Sully to run this with me live?",
    a: "Fill in as much as you can, then book a working call. We'll pressure-test your clusters against real search data and lock the 90-day calendar together.",
  },
];

const CONTENTS = [
  "Section 01  ·  Positioning",
  "Section 02  ·  Money Pages",
  "Section 03  ·  Keyword Clusters (A / B / C / D)",
  "Section 04  ·  90-Day Publish Calendar (3 waves)",
  "Section 05  ·  Single-Post Worksheet",
  "Section 06  ·  Off-Site PR Angles",
  "Section 07  ·  90-Day KPIs",
];

const PRTemplate = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Free PR Strategy Content Plan Template (PDF) | Dr. Sully"
        description="The 11-page PR &amp; SEO content-plan worksheet Dr. Sully uses with founders, keynote speakers, and coaches. Four clusters, 10 posts, 90 days. Free PDF."
        path="/pr-template"
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CreativeWork",
              name: "PR Strategy Content Plan Template",
              about: "PR and SEO content strategy planning worksheet",
              learningResourceType: "Worksheet",
              educationalUse: "Content strategy planning",
              inLanguage: "en",
              author: {
                "@type": "Person",
                name: "Dr. RD 'Sully' Sullivan",
                url: "https://drsullyspeaks.com/about",
              },
              isAccessibleForFree: true,
              url: "https://drsullyspeaks.com/pr-template",
            },
            {
              "@type": "FAQPage",
              mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }}
      />
      <SiteNav />

      <main id="main">
        {/* HERO */}
        <section className="relative pt-40 md:pt-48 pb-16 md:pb-24 bg-hero overflow-hidden">
          <div className="container max-w-6xl grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
            <Reveal variant="up">
              <span className="eyebrow">Free · 11-page worksheet</span>
              <h1 className="font-display font-bold uppercase text-white text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05]">
                The PR strategy<br />
                <span className="text-gradient-gold">template</span> Dr. Sully<br />
                actually uses.
              </h1>
              <p className="text-silver text-base md:text-lg mt-6 max-w-xl leading-relaxed">
                Four keyword clusters. Ten posts. A 90-day calendar and a
                one-page KPI scoreboard. The exact worksheet used inside
                private engagements — yours in exchange for an email.
              </p>

              <ul className="mt-8 space-y-3.5">
                {SECTIONS.map(({ icon: Icon, label, body }) => (
                  <li key={label} className="flex items-start gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-gold/15 text-gold flex-shrink-0 mt-0.5">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="text-white text-sm font-display uppercase tracking-wide">
                        {label}
                      </div>
                      <div className="text-silver text-sm leading-relaxed mt-0.5">
                        {body}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal variant="fade">
              <PRTemplateForm sourcePage="pr-template" />
            </Reveal>
          </div>
        </section>

        {/* WHAT'S INSIDE */}
        <section className="py-20 md:py-24 border-t border-border">
          <div className="container max-w-4xl">
            <div className="text-center">
              <span className="eyebrow !mx-auto">What's inside</span>
              <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-4 leading-tight">
                Seven working sections.<br />
                <span className="text-gradient-gold">Zero fluff.</span>
              </h2>
              <p className="text-silver mt-5 max-w-xl mx-auto leading-relaxed">
                Print it, drop it into Notion, or fill it in the PDF reader.
                Every page is a working surface — not a slide to skim.
              </p>
            </div>

            <ul className="mt-12 grid md:grid-cols-2 gap-px bg-border border border-border">
              {CONTENTS.map((line) => (
                <li key={line} className="bg-navy p-6 flex items-center gap-3">
                  <CheckCircle2 className="h-4 w-4 text-gold flex-shrink-0" />
                  <span className="text-white text-sm md:text-base">
                    {line}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* WHY / FROM DR. SULLY */}
        <section className="py-20 md:py-24 border-t border-border bg-navy-deep">
          <div className="container max-w-3xl">
            <span className="eyebrow">From Dr. Sully</span>
            <h2 className="font-display uppercase text-white text-3xl md:text-4xl mt-4 leading-tight">
              Why I'm giving this away.
            </h2>
            <div className="mt-6 space-y-5 text-silver text-base md:text-lg leading-relaxed">
              <p>
                Most PR plans die in a Google Doc. They start ambitious, hit
                the third week, and quietly turn into a to-do list nobody
                owns. The template you're about to download is the antidote —
                it forces every keyword cluster to connect to a money page,
                every post to link to a sibling, and every KPI to fit on one
                page you review weekly.
              </p>
              <p>
                It's the same worksheet I run inside private engagements with
                founders, keynote speakers, and pastors leading fast-growing
                organizations. If you fill it in and want a live session to
                pressure-test it against real Semrush data,{" "}
                <Link
                  to="/inquire"
                  className="text-gold hover:text-gold-light underline decoration-gold/40"
                >
                  book a working call here
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-24 border-t border-border">
          <div className="container max-w-3xl">
            <Reveal variant="up">
              <div className="text-center">
                <span className="eyebrow !mx-auto">FAQ</span>
                <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-4 leading-tight">
                  PR strategy questions,<br />
                  <span className="text-gradient-gold">answered.</span>
                </h2>
                <p className="text-silver mt-5 max-w-xl mx-auto leading-relaxed">
                  The questions founders, speakers, and comms leads ask before
                  they download. If yours isn't here, grab the template — the
                  answer is probably in section 05.
                </p>
              </div>

              <Accordion type="single" collapsible className="mt-12 border border-border bg-navy divide-y divide-border">
                {FAQS.map((f, i) => (
                  <AccordionItem key={f.q} value={`item-${i}`} className="border-0 px-6">
                    <AccordionTrigger className="text-left text-white font-display uppercase text-sm md:text-base tracking-wide hover:no-underline py-5">
                      <span className="flex items-start gap-3">
                        <HelpCircle className="h-4 w-4 text-gold flex-shrink-0 mt-1" />
                        <span>{f.q}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-silver text-base leading-relaxed pl-7 pb-5">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <div className="mt-10 text-center">
                <p className="text-silver text-sm">
                  Ready to fill it in?{" "}
                  <a
                    href="#main"
                    className="text-gold hover:text-gold-light underline decoration-gold/40"
                  >
                    Register above to download the PDF
                  </a>{" "}
                  — or{" "}
                  <Link
                    to="/inquire"
                    className="text-gold hover:text-gold-light underline decoration-gold/40"
                  >
                    book a working call with Dr. Sully
                  </Link>
                  .
                </p>
              </div>
            </Reveal>
          </div>
        </section>


        {/* FINAL CTA */}
        <section className="py-24 border-t border-border">
          <div className="container max-w-3xl text-center">
            <Reveal variant="up">
              <span className="eyebrow !mx-auto">Grab it</span>
              <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-4 leading-tight">
                One email.<br />
                <span className="text-gradient-gold">11 pages of plan.</span>
              </h2>
              <div className="mt-10 max-w-xl mx-auto">
                <PRTemplateForm sourcePage="pr-template-footer" />
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default PRTemplate;
